/**
 * Per-query loading and caching for screened eligibility worklist tabs.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  clinicalTrialsService,
  type EligibilityScreenedRowsQuery,
} from "../api/clinicalTrialsService";
import {
  eligibilityRowToTableRow,
  type EligibilityRowDisplayCatalogs,
  type EligibilityTableRow,
} from "../data-model/eligibilityRows";

const REQUEST_TIMEOUT_MS = 20000;

/** Loaded table state for one trial/tab/filter/page combination. */
export interface EligibilityRowsState {
  rows: EligibilityTableRow[];
  totalCount: number;
  page: number;
  totalPages: number;
  hitsPerPage: number;
  loading: boolean;
  error: string | null;
  refresh: () => void;
  /** Clears every cached tab/query and re-fetches the active worklist. */
  refreshAll: () => void;
}

type CachedEligibilityRowsState = Omit<
  EligibilityRowsState,
  "refresh" | "refreshAll"
>;

const EMPTY_STATE: CachedEligibilityRowsState = {
  rows: [],
  totalCount: 0,
  page: 0,
  totalPages: 0,
  hitsPerPage: 50,
  loading: false,
  error: null,
};

/**
 * Fetch a screened-patient page while retaining independent state per tab/query.
 *
 * Previously loaded tabs remain cached when another tab is selected. A new
 * search, filter, sort, or page creates its own request key and loading state.
 *
 * @param query - One or more API chunks needed by the current display page
 * @param catalogs - Optional options-API labels for coded row fields
 * @returns Current table rows, pagination metadata, loading/error state, and refresh
 */
export function useEligibilityScreenedRows(
  query: EligibilityScreenedRowsQuery | EligibilityScreenedRowsQuery[] | null,
  catalogs: EligibilityRowDisplayCatalogs = {},
): EligibilityRowsState {
  const [cache, setCache] = useState<
    Record<string, CachedEligibilityRowsState>
  >({});
  const [refreshToken, setRefreshToken] = useState(0);
  const loadedKeys = useRef(new Set<string>());
  const serializedQuery = query ? JSON.stringify(query) : "";
  const requestGroupKey = serializedQuery;
  const currentQuery = useRef(query);
  currentQuery.current = query;
  const catalogsRef = useRef(catalogs);
  catalogsRef.current = catalogs;

  useEffect(() => {
    const value = currentQuery.current;
    const queries = value ? (Array.isArray(value) ? value : [value]) : [];
    const pending = queries
      .map((request) => ({ request, key: JSON.stringify(request) }))
      .filter(({ key }) => !loadedKeys.current.has(key));
    if (pending.length === 0) return;

    const requests = pending.map(({ request, key }) => {
      const controller = new AbortController();
      let timedOut = false;
      const timer = window.setTimeout(() => {
        timedOut = true;
        controller.abort();
      }, REQUEST_TIMEOUT_MS);

      clinicalTrialsService
        .getEligibilityScreenedRows(request, controller.signal)
        .then((response) => {
          loadedKeys.current.add(key);
          setCache((current) => ({
            ...current,
            [key]: {
              rows: response.hits.map((hit) =>
                eligibilityRowToTableRow(hit, catalogsRef.current),
              ),
              totalCount: response.total_count,
              page: response.page,
              totalPages: response.total_pages,
              hitsPerPage: response.hits_per_page,
              loading: false,
              error: null,
            },
          }));
        })
        .catch((reason: unknown) => {
          if (controller.signal.aborted && !timedOut) return;
          setCache((current) => ({
            ...current,
            [key]: {
              ...(current[key] ?? EMPTY_STATE),
              loading: false,
              error: timedOut
                ? "Eligibility rows request timed out"
                : reason instanceof Error
                  ? reason.message
                  : "Failed to load eligibility rows",
            },
          }));
        })
        .finally(() => window.clearTimeout(timer));

      return { controller, timer };
    });

    setCache((current) => {
      const next = { ...current };
      pending.forEach(({ key }) => {
        next[key] = {
          ...(next[key] ?? EMPTY_STATE),
          loading: true,
          error: null,
        };
      });
      return next;
    });

    return () => {
      requests.forEach(({ controller, timer }) => {
        window.clearTimeout(timer);
        controller.abort();
      });
    };
  }, [refreshToken, requestGroupKey]);

  const requestKeys = useMemo(() => {
    if (!query) return [];
    return (Array.isArray(query) ? query : [query]).map((request) =>
      JSON.stringify(request),
    );
  }, [query]);

  /**
   * Invalidate and re-fetch only the currently visible query.
   *
   * @returns Nothing
   */
  const refresh = useCallback(() => {
    if (requestKeys.length === 0) return;
    requestKeys.forEach((key) => loadedKeys.current.delete(key));
    setRefreshToken((current) => current + 1);
  }, [requestKeys]);

  /**
   * Drop all cached worklist pages (every tab/filter) and reload the current view.
   *
   * @returns Nothing
   */
  const refreshAll = useCallback(() => {
    loadedKeys.current.clear();
    setCache({});
    setRefreshToken((current) => current + 1);
  }, []);

  const currentStates = requestKeys.map(
    (key) => cache[key] ?? { ...EMPTY_STATE, loading: true },
  );
  const metadata = currentStates.find((state) => !state.loading) ?? EMPTY_STATE;
  return {
    ...metadata,
    rows: currentStates.flatMap((state) => state.rows),
    totalCount: Math.max(0, ...currentStates.map((state) => state.totalCount)),
    loading: currentStates.some((state) => state.loading),
    error: currentStates.find((state) => state.error !== null)?.error ?? null,
    refresh,
    refreshAll,
  };
}
